/**
 * O cliente do serviço `integracao-erp` — as credenciais do Carga Máquina (11.2, AD-30), os
 * recebimentos do conector (FR-72) e a tela espelho do Orçamento (FR-43).
 *
 * O módulo espelha o serviço, um a um. **Nenhum tipo de contrato é digitado aqui** (AD-16): todos
 * são apelidos dos schemas gerados em `src/api/tipos-gerados/integracao-erp.ts`. Alterou o contrato
 * do serviço, rode `npm run gerar-tipos` — o CI reprova a divergência.
 *
 * **Três recursos, e três chaves de cache.** `GET /integracoes` responde o estado das
 * credenciais (11.2 — o prazo do ERP saiu da tela e virou variável de ambiente);
 * `GET /recebimentos` responde uma lista que muda sozinha (o desfecho de um recebimento chega por
 * evento, do `negocios`); `GET /negocios/{id}/orcamentos` responde o espelho de **um** Negócio.
 * Nenhum deles compartilha chave e nenhum invalida outro: trocar uma credencial não move
 * recebimento nenhum, e o espelho só muda quando o ERP empurra de novo — não há mutação no SPA que
 * possa mexer nele.
 *
 * **O que este módulo não tem é a consulta de CNPJ**, e a ausência é o desenho do épico: o SPA
 * não fala com o `integracao-erp` para consultar documento. Quem pergunta é o `cadastro`, pelo
 * canal interno `/interno/*` (AD-24) — que o gateway bloqueia de fora —, porque quem é dono da
 * ficha e da ida dela ao Carga Máquina é ele. Do SPA, a consulta é `POST /empresas/consulta-de-documento`,
 * em `modulos/cadastro/api.ts`.
 *
 * As funções são chamadas autenticadas comuns: nenhuma passa `semRenovacao`, porque um acesso
 * expirado no meio de uma edição deve se resolver sozinho, sem jogar a pessoa de volta para a tela
 * de login com o trabalho perdido.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { chamar } from '@/api/cliente'
import type { components } from '@/api/tipos-gerados/integracao-erp'

/** Os schemas do `openapi.json` do `integracao-erp`, gerados por `npm run gerar-tipos` (AD-16). */
type Esquemas = components['schemas']

/* --- As credenciais do Carga Máquina (história 11.2, AD-30, FR-82) -------------------- */

/**
 * O que `GET /integracoes` responde: o bloco de credenciais **ou `null`**.
 *
 * O bloco só vem para quem tem `editar_credenciais_de_integracao` (FR-82: "as credenciais são só
 * do Admin"); para os demais perfis ele chega `null`, e a tela não desenha detalhe nenhum. De cada
 * credencial vêm, no máximo, os **últimos caracteres** — o segredo inteiro nunca sai do serviço.
 */
export type IntegracoesDoErp = Esquemas['RespostaDeIntegracoesDoErp']

/** O bloco de credenciais, quando ele vem. */
export type CredenciaisDoErp = Esquemas['CredenciaisDoErp']

/** O estado de uma credencial: configurada ou não, os últimos caracteres e quando mudou. */
export type EstadoDaCredencialDoErp = Esquemas['EstadoDaCredencial']

/**
 * Chave de cache das credenciais do ERP.
 *
 * **O prazo de leitura do ERP não tem mais chave, nem tela** (11.2): ele virou a variável de
 * ambiente `PRAZO_DO_ERP_EM_SEGUNDOS` (AD-6, FR-82), e `GET/PUT /configuracao` saíram do serviço.
 */
export const CHAVE_DAS_INTEGRACOES_DO_ERP = ['integracao-erp', 'integracoes'] as const

/** Lê o estado das credenciais (`200`; `credenciais: null` para quem não pode vê-las). */
export function lerIntegracoesDoErp(): Promise<IntegracoesDoErp> {
  return chamar<IntegracoesDoErp>('integracao-erp', '/integracoes')
}

/**
 * Troca o token da API do Carga Máquina (`200`). A próxima chamada do adaptador já usa o novo —
 * sem reinício. `422` para valor vazio, `403` sem a permissão, `503` sem a chave de cifra.
 */
export function trocarTokenDaApiDoErp(token: string): Promise<IntegracoesDoErp> {
  return chamar<IntegracoesDoErp>('integracao-erp', '/integracoes/token-da-api', {
    method: 'PUT',
    corpo: { token } satisfies Esquemas['CorpoDoTokenDaApi'],
  })
}

/** Troca o segredo do conector (`200`). A próxima chamada do conector é validada pelo novo. */
export function trocarSegredoDoConector(segredo: string): Promise<IntegracoesDoErp> {
  return chamar<IntegracoesDoErp>('integracao-erp', '/integracoes/segredo-do-conector', {
    method: 'PUT',
    corpo: { segredo } satisfies Esquemas['CorpoDoSegredoDoConector'],
  })
}

/**
 * As credenciais do ERP. `habilitada` fecha a porta antes da rede para quem não tem a permissão
 * de credenciais — a resposta viria `null` de qualquer forma, e não há nada a mostrar.
 */
export function useIntegracoesDoErp({ habilitada = true }: { habilitada?: boolean } = {}) {
  return useQuery({
    queryKey: CHAVE_DAS_INTEGRACOES_DO_ERP,
    queryFn: lerIntegracoesDoErp,
    enabled: habilitada,
    retry: false,
  })
}

/** Qual credencial do ERP trocar, e o valor novo. */
export type TrocaDeCredencialDoErp =
  | { credencial: 'token_da_api'; valor: string }
  | { credencial: 'segredo_do_conector'; valor: string }

/**
 * Troca uma credencial e **grava a resposta no cache**: ela já é o estado completo depois da
 * troca, com os últimos caracteres novos — reler seria uma ida a mais para a mesma resposta.
 */
export function useTrocarCredencialDoErp() {
  const clienteDeConsultas = useQueryClient()
  return useMutation({
    mutationFn: (troca: TrocaDeCredencialDoErp) =>
      troca.credencial === 'token_da_api'
        ? trocarTokenDaApiDoErp(troca.valor)
        : trocarSegredoDoConector(troca.valor),
    onSuccess: (resposta) => {
      clienteDeConsultas.setQueryData(CHAVE_DAS_INTEGRACOES_DO_ERP, resposta)
    },
  })
}

/**
 * Um recebimento do conector do ERP, como as rotas de Admin o devolvem (5.2, FR-72).
 *
 * **Não há `payload` aqui**, e a ausência é do serviço: o corpo cru do ERP fica gravado — é a
 * evidência que o FR-72 pede — e não sai em resposta nenhuma. A tela mostra o que decide: o código
 * do Negócio, qual Orçamento, o desfecho e, quando houve recusa, o motivo.
 */
export type RecebimentoDoConector = Esquemas['RespostaDoRecebimento']

/** Os quatro desfechos possíveis, como o serviço os declara — a lista não é digitada aqui. */
export type SituacaoDoRecebimento = Esquemas['SituacaoDoRecebimento']

/**
 * A janela que a tela pede.
 *
 * O serviço tem teto (`limite <= 200`) e um padrão de 50, e a lista **só cresce**: com o conector
 * exportando de verdade, o recebimento rejeitado mais antigo sairia da primeira página. Sem pedir a
 * janela, a tela não teria como saber que ele existe — e o Orçamento perdido ficaria invisível
 * dentro da própria tela que existe para mostrá-lo.
 */
export interface Paginacao {
  limite?: number
  deslocamento?: number
}

/**
 * Chave de cache dos recebimentos — **prefixo**, e o filtro e a página entram como sufixo.
 *
 * `['integracao-erp', 'recebimentos', <situação|'todas'>, <deslocamento>]`: cada combinação tem a
 * sua entrada, e invalidar o prefixo alcança todas de uma vez. É o que faz um reprocesso repintar a
 * lista aberta **e** a página seguinte que a pessoa vai abrir — sem isso, voltar para "Todos"
 * depois de reprocessar mostraria a situação antiga por até o `staleTime`.
 */
export const CHAVE_DOS_RECEBIMENTOS = ['integracao-erp', 'recebimentos'] as const

/**
 * O que a tela pede por página.
 *
 * Ela **pede** o limite em vez de aceitar o padrão do serviço (50) pela mesma razão de
 * `ListaDeEmpresas`: é ela quem desenha a paginação, e um padrão que mudasse no backend faria a
 * tela contar páginas de um tamanho e receber outro. Vinte é o que cabe num cartão da tela de
 * Administração sem transformá-lo na tela inteira.
 */
export const LIMITE_DOS_RECEBIMENTOS = 20

/** A chave de uma consulta concreta. `undefined` é "sem filtro", e ele tem entrada própria. */
export function chaveDosRecebimentos(situacao?: SituacaoDoRecebimento, deslocamento = 0) {
  return [...CHAVE_DOS_RECEBIMENTOS, situacao ?? 'todas', deslocamento] as const
}

/**
 * Lista os recebimentos do conector (`200`), mais recentes primeiro.
 *
 * Exige **perfil de gestão** (Admin ou Gestor comercial), e não uma permissão do catálogo: o
 * catálogo do FR-61-A é lista fechada e não tem linha para os recebimentos do conector. Vendedor e
 * SDR recebem `403` — e o cartão não é montado para eles.
 *
 * O filtro por situação é opcional. Ele vai na query só quando existe: mandar `situacao=` vazio
 * seria um valor fora do enum, e o serviço responderia `422`.
 */
export function listarRecebimentos(
  situacao?: SituacaoDoRecebimento,
  { limite = LIMITE_DOS_RECEBIMENTOS, deslocamento = 0 }: Paginacao = {},
): Promise<{ itens: RecebimentoDoConector[] }> {
  const parametros = new URLSearchParams({
    limite: String(limite),
    deslocamento: String(deslocamento),
  })
  // O filtro entra **só quando existe**: `?situacao=` vazio é um valor fora do enum, e o serviço
  // responderia `422` — a lista some, e quem olha não descobre por quê.
  if (situacao !== undefined) {
    parametros.set('situacao', situacao)
  }
  return chamar<{ itens: RecebimentoDoConector[] }>(
    'integracao-erp',
    `/recebimentos?${parametros.toString()}`,
  )
}

/**
 * Reprocessa um recebimento **rejeitado** (`200`), a partir do payload já guardado.
 *
 * Nada é reenviado pelo conector — e é por isso que ele resolve o caso real: o Orçamento chegou
 * antes de o Negócio existir, alguém criou o Negócio, e agora o código casa. A situação volta a
 * `em_processamento` (e o vínculo é pedido ao `negocios`) ou fica `rejeitado` com o motivo novo.
 *
 * `403` para quem não é gestão; `404` quando o recebimento não existe; `409` quando ele não está
 * rejeitado. Mostre a mensagem do serviço como ela veio.
 */
export function reprocessarRecebimento(recebimentoId: string): Promise<RecebimentoDoConector> {
  return chamar<RecebimentoDoConector>(
    'integracao-erp',
    `/recebimentos/${encodeURIComponent(recebimentoId)}/reprocessar`,
    { method: 'POST' },
  )
}

/**
 * A lista de recebimentos, por filtro.
 *
 * `retry: false` como as demais consultas do CRM: a recusa esperada aqui é o `403` de quem não é
 * gestão — que não muda por insistir — e o `401` de sessão expirada, que o cliente já renova
 * sozinho antes de deixar o erro subir.
 *
 * **`habilitada` fecha a porta antes da rede, e ela não é otimização.** O componente decide não se
 * montar para quem não é gestão (as duas rotas respondem `403`, a leitura inclusive), mas um hook
 * roda antes de qualquer `return null` — sem esta chave a tela mandaria uma requisição que ela já
 * sabe que voltaria recusada, para todo Vendedor que abrisse `/integracoes`.
 */
export function useRecebimentosDoConector(
  situacao?: SituacaoDoRecebimento,
  { habilitada = true, deslocamento = 0 }: { habilitada?: boolean; deslocamento?: number } = {},
) {
  return useQuery({
    queryKey: chaveDosRecebimentos(situacao, deslocamento),
    queryFn: () => listarRecebimentos(situacao, { deslocamento }),
    enabled: habilitada,
    retry: false,
  })
}

/**
 * Um Orçamento espelho de um Negócio (5.3, FR-43).
 *
 * **`valor` tem três estados no tipo, e os três dizem coisas diferentes.** `"1500.50"` é o valor que
 * o ERP mandou, `null` é "o ERP não mandou valor" (o contrato do conector não o exige) e a chave
 * **ausente** é "não é da sua conta" — o recorte da SDR (FR-62). O gerador escreve isso como
 * `valor?: string | null`, e é por isso que a tela lê os **três** por `textoDoValor`, e não por um
 * `?? '—'` que colapsaria os dois últimos.
 *
 * Ele é somente leitura: não há função de criar, editar nem excluir Orçamento neste módulo, porque
 * não há rota. O que muda o Orçamento é o Carga Máquina.
 */
export type OrcamentoDoNegocio = Esquemas['RespostaDoOrcamento']

/**
 * Uma linha do Orçamento, como a tela a desenha.
 *
 * Os dois campos de dinheiro (`preco_unitario` e `valor_total`) são opcionais pelo mesmo mecanismo
 * do `valor` acima: para a SDR eles não chegam.
 */
export type ItemDoOrcamento = Esquemas['RespostaDoItemDoOrcamento']

/**
 * Chave de cache do espelho — **com o `negocio_id` dentro**.
 *
 * Sem o id na chave, abrir a ficha de outro Negócio reusaria o cache do anterior e o cartão mostraria
 * o Orçamento errado até o `staleTime` vencer. É o mesmo cuidado de `chaveDosRecebimentos`, onde o
 * filtro e a página entram como sufixo.
 */
export const CHAVE_DOS_ORCAMENTOS = ['integracao-erp', 'orcamentos'] as const

/** A chave de uma consulta concreta: o espelho **daquele** Negócio. */
export function chaveDosOrcamentos(negocioId: string) {
  return [...CHAVE_DOS_ORCAMENTOS, negocioId] as const
}

/**
 * Lê os Orçamentos espelho de um Negócio (`200`), mais recentes primeiro.
 *
 * Exige a permissão `ver_espelho_de_orcamento` do catálogo do FR-61-A, que os quatro perfis têm —
 * `limitada` para Vendedor e SDR, e o recorte de `limitada` é de **linha**: quem não enxerga o
 * Negócio recebe `200` com a lista vazia, e nunca `403`. Um `403` contaria a existência do Negócio.
 *
 * **Nada aqui fala com o ERP.** A rota lê a tabela do CRM: o Carga Máquina fora do ar não muda esta
 * tela, e o espelho só muda quando o conector empurra de novo (FR-46).
 */
export function listarOrcamentosDoNegocio(
  negocioId: string,
): Promise<{ itens: OrcamentoDoNegocio[] }> {
  return chamar<{ itens: OrcamentoDoNegocio[] }>(
    'integracao-erp',
    `/negocios/${encodeURIComponent(negocioId)}/orcamentos`,
  )
}

/**
 * O espelho de um Negócio.
 *
 * `retry: false` como as demais consultas do CRM: a recusa esperada aqui é o `403` de quem teve a
 * permissão negada — que não muda por insistir — e o `401` de sessão expirada, que o cliente já
 * renova sozinho antes de deixar o erro subir.
 *
 * **`habilitada` fecha a porta antes da rede**, pelo mesmo motivo de `useRecebimentosDoConector`: um
 * hook roda antes de qualquer `return null` do componente, e sem esta chave a ficha mandaria uma
 * requisição sobre um `negocioId` vazio enquanto a rota ainda não resolveu o parâmetro.
 */
export function useOrcamentosDoNegocio(
  negocioId: string,
  { habilitada = true }: { habilitada?: boolean } = {},
) {
  return useQuery({
    queryKey: chaveDosOrcamentos(negocioId),
    queryFn: () => listarOrcamentosDoNegocio(negocioId),
    enabled: habilitada && negocioId !== '',
    retry: false,
  })
}

/**
 * Reprocessa e relê a lista.
 *
 * Invalidar o **prefixo**, e não remendar o cache com a resposta, é o ponto: o desfecho do
 * reprocesso pode ser `em_processamento` (e mudar de novo quando o `negocios` responder, sem que a
 * tela seja avisada) ou `rejeitado` com outro motivo. A releitura é o que mantém o cartão mostrando
 * o que o serviço tem, e não o que a tela supôs — e o prefixo alcança também a página seguinte e a
 * lista filtrada que a pessoa vai abrir em seguida.
 *
 * **`onSettled`, e não `onSuccess`** — e a diferença é justamente o caso em que a tela está errada.
 * As duas recusas esperadas aqui (`409` e `404`) dizem a mesma coisa: **a linha mudou desde a
 * última leitura**. Um `409` significa que ela saiu de `rejeitado` (a resposta do `negocios`
 * chegou, ou outra pessoa reprocessou antes); um `404`, que ela sumiu. Invalidar só no sucesso
 * deixaria a linha velha na tela com o botão ativo, e o clique seguinte repetiria o mesmo erro — a
 * tela insistindo num estado que o serviço já não tem. Relendo nos dois caminhos, o erro aparece
 * **uma vez** e a lista passa a mostrar o que existe.
 *
 * **Nenhuma outra chave é invalidada**, e a ausência é o que o desenho promete: o vínculo nasce do
 * outro lado da fronteira, por evento, e nada no SPA lê aquela tabela.
 */
export function useReprocessarRecebimento() {
  const clienteDeConsultas = useQueryClient()
  return useMutation({
    mutationFn: (recebimentoId: string) => reprocessarRecebimento(recebimentoId),
    onSettled: () =>
      clienteDeConsultas.invalidateQueries({
        queryKey: CHAVE_DOS_RECEBIMENTOS,
      }),
  })
}

/**
 * Um Material do Carga Máquina, como a busca de Produto o desenha (5.6, FR-44).
 *
 * `preco` é **opcional** pelo mesmo mecanismo do `valor` do Orçamento: para a SDR a chave não
 * chega, e o serviço a omite em vez de mandá-la nula (FR-62). E ele também é anulável: `venda` é
 * opcional no schema do ERP, e Material sem preço de venda cadastrado existe.
 *
 * **Nenhum campo de custo**, e não há de onde: o adaptador do serviço saneia a resposta do ERP na
 * borda, antes de ela virar objeto (AD-3).
 */
export type MaterialDoErp = Esquemas['RespostaDoMaterial']

/**
 * Os dois filtros da busca — **cada um opcional, e ao menos um preenchido**.
 *
 * Eles são dois, e não um texto só mandado nos dois campos, porque `GET /material` do Carga Máquina
 * combina os filtros por **E**, e não por OU: mandar `?codigo=paraf&nome=paraf` pede o Material cujo
 * código **e** nome contenham "paraf" — que na prática não existe, e a busca nunca acha nada. É o
 * contrato que a rota já publica (os dois `required: false`), lido ao pé da letra.
 */
export interface FiltroDeMaterial {
  codigo?: string
  nome?: string
}

/**
 * Chave de cache da busca — **com o `negocio_id` e os dois filtros dentro**.
 *
 * O `negocio_id` entra porque a rota é dele (não há catálogo de Produtos fora do Negócio, FR-44), e
 * os filtros entram porque cada par digitado é uma resposta diferente: sem eles, trocar de
 * "parafuso" para "porca" reusaria o cache do anterior e a lista mostraria o resultado errado até o
 * `staleTime` vencer.
 *
 * Os dois entram **separados**, e não concatenados: `{codigo: 'a-b'}` e `{nome: 'a-b'}` são buscas
 * diferentes, e uma chave que os juntasse num texto só faria as duas compartilharem resposta.
 */
export const CHAVE_DOS_MATERIAIS = ['integracao-erp', 'materiais'] as const

/** A chave de uma busca concreta: aquele Negócio, aquele par de filtros. */
export function chaveDosMateriais(negocioId: string, filtro: FiltroDeMaterial) {
  return [...CHAVE_DOS_MATERIAIS, negocioId, filtro.codigo ?? '', filtro.nome ?? ''] as const
}

/**
 * Diz se há filtro o bastante para a rota aceitar: ao menos um dos dois, **não vazio**.
 *
 * O operador é `||`, e não `??`, e a diferença não é estilo: `??` só cai no segundo lado quando o
 * primeiro é `null`/`undefined`, e o campo vazio é `''` — com `??`, uma busca só por nome (com o
 * código em branco) responderia "não há filtro" e a consulta nunca sairia.
 */
export function temFiltroDeMaterial(filtro: FiltroDeMaterial): boolean {
  return Boolean(filtro.codigo || filtro.nome)
}

/**
 * Busca Produtos no Carga Máquina, **dentro** de um Negócio (`200`).
 *
 * **Só os filtros preenchidos vão na query**, e essa é a correção que a 5.6 precisou fazer: os dois
 * campos do lado do ERP são busca **parcial** e se combinam por **E**. Mandar o mesmo texto nos dois
 * — o que uma primeira versão desta função fazia, para que um campo só servisse — pede o Material
 * cujo código **e** nome contenham aquela string, e o resultado é uma busca que não acha nada.
 * Mandar só o que foi digitado é o que faz "parafuso" no campo Nome trazer parafusos.
 *
 * **Esta é a única função do SPA que faz um serviço falar com o Carga Máquina no ato.** O ERP fora
 * do ar é `503` com a mensagem do FR-47, e a tela a mostra verbatim — ela não a reescreve.
 *
 * `422` quando nenhum dos dois vem preenchido, com a mensagem que nomeia o que falta — a tela não
 * chega a gastar a chamada, mas a recusa existe no serviço para quem chegar por outra porta.
 *
 * Exige a permissão `ver_produtos_do_negocio` do catálogo do FR-61-A, que os quatro perfis têm.
 * Negócio que a pessoa não enxerga responde `200` com a lista vazia, e nunca `403`: um `403`
 * contaria a existência do Negócio.
 */
export function buscarMateriais(
  negocioId: string,
  filtro: FiltroDeMaterial,
): Promise<{ itens: MaterialDoErp[] }> {
  const parametros = new URLSearchParams()
  if (filtro.codigo) {
    parametros.set('codigo', filtro.codigo)
  }
  if (filtro.nome) {
    parametros.set('nome', filtro.nome)
  }
  return chamar<{ itens: MaterialDoErp[] }>(
    'integracao-erp',
    `/negocios/${encodeURIComponent(negocioId)}/materiais?${parametros.toString()}`,
  )
}

/**
 * A busca de Produto no ERP.
 *
 * `retry: false` como as demais consultas do CRM, e aqui ela pesa: a recusa esperada é o `503` do
 * FR-47, e insistir três vezes seguraria a tela por três prazos do ERP antes de dizer o que já se
 * sabia na primeira.
 *
 * **`habilitada` fecha a porta antes da rede**, e ela é o que impede a tela de gastar uma ida ao
 * Carga Máquina a cada tecla: o cartão só liga a consulta quando há texto o bastante — e já
 * **estabilizado** por debounce —, e o hook roda antes de qualquer `return null` do componente.
 */
export function useBuscaDeMateriais(
  negocioId: string,
  filtro: FiltroDeMaterial,
  { habilitada = true }: { habilitada?: boolean } = {},
) {
  return useQuery({
    queryKey: chaveDosMateriais(negocioId, filtro),
    queryFn: () => buscarMateriais(negocioId, filtro),
    enabled: habilitada && negocioId !== '' && temFiltroDeMaterial(filtro),
    retry: false,
  })
}
